import { ProducerAssignmentRole, type ProgramType } from '@newfront-insurance/account-api';
import { AuthRole } from '@newfront-insurance/auth-api';
import type { DomainObjectContext } from '@newfront-insurance/dod-api';
import groupBy from 'lodash/groupBy';

import { FolderTypes } from './features/folders-tree/helpers/types';
import type { FolderAssociationData } from './types';
import type { ServicingTeamItem } from '../../accounts/client/queries/use-servicing-team';
import type { NewFolderData } from '../types';
import { ObjectReferenceType } from '../types';
import type { SelectedEntity } from '../utils/map-object-refs';

export function getParentsIndexes(index: string): string[] {
  const parentsIndexes: string[] = [];

  if (index.includes('.')) {
    // if a child element, select all parents as well
    const split = index.toString().split('.');
    for (let i = 1; i < split.length - 1; i += 1) {
      parentsIndexes.push(split.slice(0, i + 1).join('.'));
    }
  }
  return parentsIndexes;
}

// type guard
export function isFolderAssociationData(
  association: DomainObjectContext | FolderAssociationData,
): association is FolderAssociationData {
  return !!(association as FolderAssociationData).parentFolderType;
}

export const groupAndSortAssociationFoldersByYear = <
  T extends { parentFolderType: string; associatedYears?: number[] },
>(
  items: T[] | undefined,
): T[] => {
  if (!items?.length) return [];

  return Object.values(groupBy(items, 'parentFolderType')).flatMap((group) =>
    group.sort((a, b) => {
      const yearsA = [...(a.associatedYears ?? [])].sort((x, y) => y - x).join();
      const yearsB = [...(b.associatedYears ?? [])].sort((x, y) => y - x).join();

      return yearsB.localeCompare(yearsA);
    }),
  );
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export enum SEGMENT_FILTER_ENUM {
  EMAIL = 'Emails',
  FILE = 'Files',
  ALL = 'All',
}

export const FileFilterToIsRelatedToEmailFilterMap: Record<SEGMENT_FILTER_ENUM, boolean | undefined> = {
  [SEGMENT_FILTER_ENUM.EMAIL]: true,
  [SEGMENT_FILTER_ENUM.FILE]: false,
  [SEGMENT_FILTER_ENUM.ALL]: undefined,
};

interface SplitAssociations {
  folderAssociations: SelectedEntity[];
  entityAssociations: SelectedEntity[];
}

function splitAssociations(associations: SelectedEntity[]): SplitAssociations {
  return associations.reduce(
    (acc: SplitAssociations, association) => {
      const type = association.type === FolderTypes.SUBFOLDER ? 'folderAssociations' : 'entityAssociations';
      acc[type].push(association);
      return acc;
    },
    { folderAssociations: [], entityAssociations: [] },
  );
}

export const getAnalyticsPropertiesForFilesImprovement = (
  fileId: string,
  newFolders: NewFolderData[],
  associations: SelectedEntity[],
  accountUuid?: string,
  programType?: ProgramType,
) => {
  const splittedAssociations = splitAssociations(associations);

  return {
    fileId,
    entityAssociations: splittedAssociations.entityAssociations,
    folderAssociations: [...newFolders, ...splittedAssociations.folderAssociations],
    accountUuid,
    hasAccount: splittedAssociations.entityAssociations.some(
      (association) =>
        association.type === ObjectReferenceType.ACCOUNT || association.type === ObjectReferenceType.CLIENT,
    ),
    hasInvoice: splittedAssociations.entityAssociations.some(
      (association) => association.type === ObjectReferenceType.INVOICE,
    ),
    hasFolders: newFolders.length > 0 || splittedAssociations.folderAssociations.length > 0,
    programType,
  };
};

export const PRODUCER_ROLES_TO_EXCLUDE = [
  AuthRole.CL_PRODUCER,
  AuthRole.EB_PRODUCER,
  AuthRole.PL_PRODUCER,
  AuthRole.PRODUCER,
];

/**
 * Determines if a user is a sole producer in the servicing team.
 * A sole producer has exactly one role, which is either PRIMARY_PRODUCER or SECONDARY_PRODUCER.
 */
export function isSoleProducer(servicingTeam: ServicingTeamItem[], userUuid: string): boolean {
  return servicingTeam.some((member) => {
    if (member.user.uuid !== userUuid) return false;

    const roles = member.servicingTeamRoles;
    return (
      roles?.length === 1 &&
      (roles[0].roleId === ProducerAssignmentRole.PRIMARY_PRODUCER ||
        roles[0].roleId === ProducerAssignmentRole.SECONDARY_PRODUCER)
    );
  });
}

/**
 * Filters the servicing team employees to exclude those who are ONLY producers.
 * This filter removes employees from the list if they meet the following criteria:
 * 1. They have exactly one role in their servicingTeamRoles array.
 * 2. That single role is either PRIMARY_PRODUCER or SECONDARY_PRODUCER.
 * Employees with multiple roles or any other single role are kept in the list.
 */
export function excludeProducersFromServicingTeam(
  servicingTeamEmployees: ServicingTeamItem[] | undefined,
): ServicingTeamItem[] {
  if (!servicingTeamEmployees) {
    return [];
  }

  return servicingTeamEmployees.filter((employee) => !isSoleProducer(servicingTeamEmployees, employee.user.uuid!));
}
